// The Market. Five tabs under the page title:
//   Browse   a sidebar of categories (with counts) and filters, and a board the server pages,
//            filters and sorts (shared/market-query.ts). Unique things (creatures, held items,
//            relics, shrines) are one row per listing; stackable items fold into one row per
//            item kind with the lowest price, units on offer and the 7-day average. A row of
//            those opens the item's page: price history, a buy box that fills from the
//            cheapest listings, every sell order, and selling your own.
//   Sell     everything you hold that trades, with List buttons (and the Exchange's bids).
//   Yours    your open listings.
//   History  your sales and purchases.
//   Prices   the price table (client/pages.ts priceSection).
//   Gems     the gem wallet: balance, withdrawals and history (only while gems are open).
// Currencies: every listing is priced in coins or, while the gem switch is on, gems
// (shared/gems.ts). The server says which are open (MarketDTO.curs); with one, no
// currency control shows at all.
// Fees are spelled out in the listing form (client/ui.ts listDialog), not here.
import { SPECIES } from '../shared/species.ts';
import { ITEM, ITEMS, relicName, relicLine, type Relic } from '../shared/items.ts';
import { heldName, type HeldItem } from '../shared/held.ts';
import { heldText } from './held.ts';
import { SHRINE_TIERS, shrineChance, type Shrine } from '../shared/shrines.ts';
import { traitOf } from '../shared/traits.ts';
import { ratingOf } from '../shared/rating.ts';
import { GRADE_CUTOFFS, gradeOf } from '../shared/grade.ts';
import { ELEMENTS, ELEMENT_IDS } from '../shared/elements.ts';
import { REGIONS } from '../shared/grounds.ts';
import { CONFIG } from '../shared/config.ts';
import { MARKET_MAX_OPEN } from '../shared/market.ts';
import { MARKET_CATS, MARKET_PAGE, MARKET_PER_MIN, MARKET_CURRENCIES, catOf, type MarketCat, type MarketCurrency, type MarketQuery, type MarketRow, type MarketSort } from '../shared/market-query.ts';
import { PRICE_WINDOW_DAYS } from '../shared/prices.ts';
import { GEM_WITHDRAW_MIN, cashoutUsd, gemFee, gemUsd } from '../shared/gems.ts';
import { money, curIcon, curWord } from './money.ts';
import { packById } from '../shared/diamond-packs.ts';
import type { ClientMsg, CreatureDTO, ListingDTO } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { spriteUrl, iconUrl } from './assets.ts';
import { esc, full, elChip, cname, left } from './fmt.ts';
import { playerLink } from './social.ts';
import { gradeBadge, relicGradeBadge, crest, auraTag } from './badges.ts';
import { provenanceLine } from './provenance.ts';
import { ICON } from './icons.ts';
import { confirmClick, isArmed, CONFIRM_MONEY_MS } from './confirm.ts';
import { go } from './router.ts';
import { priceSection, requestPrices, showPriceTable } from './pages.ts';
import { psecTabs, psecWrap, phoneSections } from './psec.ts';

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindMarket(o: { send: (m: ClientMsg) => void; render?: () => void }) { send = o.send; if (o.render) rerender = o.render; }

type Tab = 'browse' | 'item' | 'sell' | 'mine' | 'history' | 'prices' | 'gems';
/** What the page shows: the tab, the board query (kept across visits), and the item page's buy amount. */
const M = { per: 0, fopen: false, tab: 'browse' as Tab, q: { cat: 'all', sort: 'price' } as MarketQuery, icur: 'coins' as MarketCurrency, buyQty: 1, typing: 0 as ReturnType<typeof setTimeout> | 0 };

// ---------------- routes and queries ----------------
/**
 * #/market, #/market/item/:ref (coins) or :ref:gems, #/market/sell, #/market/mine, #/market/history,
 * #/market/prices, #/market/gems, #/market/:listing (a Trade card).
 */
export function openMarketRoute(sub: string, sub2: string) {
  const focus = Number(sub) || 0;
  S.marketFocus = focus;
  const [ref = '', icur] = sub2.split(':');
  if (sub === 'item' && ITEM[ref]) {
    const cur: MarketCurrency = icur === 'gems' ? 'gems' : 'coins';
    if (M.tab !== 'item' || M.q.item !== ref || M.icur !== cur) M.buyQty = 1;
    M.tab = 'item'; M.icur = cur;
    // The board keeps its own currency filter; the item page asks in its own.
    M.q = { ...M.q, item: ref };
    send({ t: 'market', q: { ...M.q, cur } });
    return;
  }
  M.tab = sub === 'sell' || sub === 'mine' || sub === 'history' || sub === 'prices' || sub === 'gems' ? sub : 'browse';
  const { item: _i, ...q } = M.q;
  ask(focus ? { ...q, focus } : { ...q, focus: undefined });
}

/** Send a query (the server answers with a page) and keep it. */
function ask(q: MarketQuery) {
  M.q = { ...q, per: M.per || undefined };
  send({ t: 'market', q: M.q });
}

/**
 * After a render of the board: how many rows fit between the first row and the bottom of the screen, so the page
 * never scrolls. When that differs from the page size asked for, ask again with it (keeping the first row in view).
 */
export function marketFit(): void {
  if (M.tab !== 'browse' || !S.market) return;
  const board = document.querySelector<HTMLElement>('.mk-board');
  const rows = board ? [...board.querySelectorAll<HTMLElement>(':scope > .mk-row')] : [];
  if (!board || !rows.length) return;
  const nav = document.getElementById('bnav');
  const navTop = nav && nav.getBoundingClientRect().height ? nav.getBoundingClientRect().top : innerHeight;
  const bottom = Math.min(innerHeight, navTop) - 10;
  const rowH = Math.max(...rows.slice(0, 6).map((r) => r.getBoundingClientRect().height));
  // Under the rows: the board's bottom edge and the pager.
  const room = bottom - rows[0].getBoundingClientRect().top - 14 - 44;
  const fit = Math.max(MARKET_PER_MIN, Math.min(MARKET_PAGE, Math.floor(room / Math.max(1, rowH))));
  const per = M.per || MARKET_PAGE;
  if (fit === per) return;
  // Fewer rows than the page holds and nothing to scroll: this is the last page, not too small a size.
  const de = document.documentElement, page = document.getElementById('page');
  const over = Math.max(de.scrollHeight - de.clientHeight, page ? page.scrollHeight - page.clientHeight : 0);
  if (fit > per && rows.length < per) return;
  if (fit > per && over <= 1 && rows.length >= S.market.total) return;
  const first = (M.q.page ?? 0) * per;
  M.per = fit;
  ask({ ...M.q, page: Math.floor(first / fit) });
}
const setQ = (patch: Partial<MarketQuery>, keepPage = false) => ask({ ...M.q, ...patch, ...(keepPage ? {} : { page: 0 }) });

// ---------------- small parts ----------------
/** The currencies open on the market (coins, and gems while they are on). */
const curs = (): MarketCurrency[] => S.market?.curs ?? ['coins'];
const curOf = (l: { cur?: 'gems' }): MarketCurrency => l.cur ?? 'coins';
/** Money in or out: the currency's icon, then the signed amount. */
const flow = (n: number, cur: MarketCurrency) => `<span class="mk-flow ${n < 0 ? 'out' : 'in'}"><img src="${curIcon(cur)}" alt="${cur}">${n < 0 ? '−' : '+'}${full(Math.abs(n))}</span>`;
/** What the viewer can spend in a currency. */
const purse = (cur: MarketCurrency) => (cur === 'gems' ? S.market?.gems?.bal ?? 0 : S.trainer?.coins ?? 0);
/** Price each: the unit price, or a whole old lot's price spread over its units. */
const each = (l: ListingDTO) => (l.unit || Math.ceil(l.price / Math.max(1, l.qty)));
const byId = (id: number) => S.market?.listings.find((l) => l.id === id);
const ago = (ts: number) => `${left(serverNow() - ts).split(' ')[0]}`;
const tradableQty = (id: string) => { const t = S.trainer; return t ? Math.max(0, (t.bag[id] ?? 0) - (t.devItems?.[id] ?? 0)) : 0; };

const CAT_ICON: Record<MarketCat, () => string> = {
  all: () => `<i class="mc-svg">${ICON.market}</i>`,
  creature: () => `<i class="mc-svg">${ICON.creatures}</i>`,
  held: () => `<img src="${iconUrl('hearthstone')}" alt="">`,
  relic: () => `<img src="${iconUrl('relic-damage')}" alt="">`,
  shrine: () => `<img src="${iconUrl('shrine-shard')}" alt="">`,
  stones: () => `<img src="${iconUrl('froststone')}" alt="">`,
  materials: () => `<img src="${iconUrl('rune-of-ascent')}" alt="">`,
  charms: () => `<img src="${iconUrl('charm-aura-1h')}" alt="">`,
  keys: () => `<img src="${iconUrl('fenrir-token')}" alt="">`,
};

/** Art, name line and detail line of one listing. */
function describe(l: ListingDTO): { pic: string; mon: boolean; name: string; sub: string; plain: string } {
  if (l.kind === 'creature') {
    const c = l.snapshot as CreatureDTO;
    const tr = traitOf(c);
    return {
      pic: spriteUrl(c.species, c.rolls.aura), mon: true, plain: cname(c),
      name: `<b>${esc(cname(c))}</b>${crest(c.rolls.bloodline)}${c.rolls.aura ? auraTag() : ''}${c.medals?.length ? `<span class="medal-chip" title="Top damage in a Guild War">War Medal ${c.medals.map((n) => `#${n}`).join(' ')}</span>` : ''}`,
      sub: `${SPECIES[c.species].elements.map(elChip).join('')}${tr ? `<span class="${tr.rare ? 'rare-name' : ''}">${tr.rare ? '◆ ' : ''}${esc(tr.name)}</span>` : ''}${c.stars ? `<span class="stars">${'★'.repeat(c.stars)}</span>` : ''}${provenanceLine(c)}`,
    };
  }
  if (l.kind === 'relic') { const r = l.snapshot as Relic; return { pic: iconUrl(`relic-${r.stat}`), mon: false, plain: relicName(r), name: `${relicGradeBadge(r, 'sm')}<b>${esc(relicName(r))}</b>`, sub: esc(relicLine(r)) }; }
  if (l.kind === 'held') { const h = l.snapshot as HeldItem; return { pic: iconUrl(h.item), mon: false, plain: heldName(h), name: `<b>${esc(heldName(h))}</b>`, sub: esc(heldText(h)) }; }
  if (l.kind === 'shrine') {
    const sh = l.snapshot as Shrine, t = SHRINE_TIERS[sh.tier];
    return { pic: iconUrl('shrine-shard'), mon: false, plain: t.name, name: `<b>${esc(t.name)}</b>`, sub: `+${Math.round(t.share * 100)}% XP · ${t.posts} ${t.posts > 1 ? 'slots' : 'slot'} · 1 in ${full(Math.round(1 / shrineChance(sh.tier)))}` };
  }
  const it = ITEM[l.ref];
  return { pic: iconUrl(l.ref), mon: false, plain: it?.name ?? l.ref, name: `<b>${esc(it?.name ?? l.ref)}</b>`, sub: esc(it?.desc ?? '') };
}

/** Who sells it: the Exchange's label, a player link, or Yours. */
function sellerHtml(l: ListingDTO) {
  if (l.mine) return '<span class="mk-you">Yours</span>';
  if (l.house) return '<span class="mk-house">Exchange</span>';
  return playerLink(l.seller, esc(l.seller));
}

/** The buy button of one listing: Buy (armed: Confirm with the price), Enter draw during the hold. */
// Playtest speed above ×1 makes an account a test account for good (shared/playtest.ts); the server refuses its trades.
const TEST_ACCOUNT = 'Test account: playtest speed was used on it, so it cannot trade. Use another account to buy.';
const testAccount = () => !!S.trainer?.playtest;

/** What a new listing's draw is, on its button's tooltip. */
const DRAW_TIP = 'New listing: everyone who enters before the timer ends gets an equal chance, and the winner buys it at this price.';

export function buyButton(l: ListingDTO, qty = 1) {
  if (l.mine) return `<button class="btn small" data-act="unlist" data-id="${l.id}">Cancel</button>`;
  const hold = l.openAt > serverNow();
  const cost = l.unit ? l.unit * qty : l.price;
  const armed = isArmed(`mk:${l.id}`);
  if (hold) return `<button class="btn small mk-draw${armed ? ' gold armed' : ''}" data-act="mk-buy" data-id="${l.id}"${l.requested ? ' disabled' : ''} title="${DRAW_TIP}">${l.requested ? 'In the draw' : armed ? `Confirm ${full(cost)}` : 'Enter draw'}</button>`;
  if (testAccount()) return `<button class="btn small" disabled title="${TEST_ACCOUNT}">Buy</button>`;
  const short = purse(curOf(l)) < cost;
  return `<button class="btn small ${armed ? 'gold armed' : 'primary'}" data-act="mk-buy" data-id="${l.id}"${short ? ` disabled title="Not enough ${curWord(curOf(l))}"` : ''}>${armed ? `Confirm ${full(cost)}` : 'Buy'}</button>`;
}

// ---------------- the board ----------------
type Col = { label: string; sort?: MarketSort; cls?: string };
/** Column headings per category: art, what, stat, level/qty, seller/avg, price, action. */
function columns(cat: MarketCat): Col[] {
  const price: Col = { label: 'Price', sort: 'price', cls: 'r' };
  if (cat === 'creature') return [{ label: 'Creature' }, { label: 'Grade', sort: 'grade' }, { label: 'Level', sort: 'level', cls: 'r' }, { label: 'Seller' }, price];
  if (cat === 'held' || cat === 'relic') return [{ label: cat === 'held' ? 'Held item' : 'Relic' }, { label: 'Quality', sort: 'quality' }, { label: '' }, { label: 'Seller' }, price];
  if (cat === 'shrine') return [{ label: 'Shrine' }, { label: '' }, { label: '' }, { label: 'Seller' }, price];
  if (cat === 'all') return [{ label: 'Listing' }, { label: '' }, { label: 'Qty', sort: 'qty', cls: 'r' }, { label: 'Seller' }, price];
  return [{ label: 'Item' }, { label: 'Listings' }, { label: 'On offer', sort: 'qty', cls: 'r' }, { label: '7-day avg', cls: 'r' }, { label: 'Starting at', sort: 'price', cls: 'r' }];
}

function headRow(cat: MarketCat) {
  const s = M.q.sort ?? 'price';
  const cell = (c: Col) => {
    if (!c.sort) return `<span class="${c.cls ?? ''}">${c.label}</span>`;
    const on = c.sort === 'price' ? s === 'price' || s === '-price' : s === c.sort;
    const arrow = c.sort === 'price' ? (s === '-price' ? '▾' : s === 'price' ? '▴' : '') : on ? '▾' : '';
    return `<span class="${c.cls ?? ''}"><button class="mk-sort${on ? ' on' : ''}" data-msort="${c.sort}">${c.label}${arrow ? `<i>${arrow}</i>` : ''}</button></span>`;
  };
  return `<div class="mk-head" aria-hidden="true"><span></span>${columns(cat).map(cell).join('')}<span></span></div>`;
}

function oneRow(l: ListingDTO, cat: MarketCat) {
  const d = describe(l);
  const hold = l.openAt > serverNow();
  const c = l.kind === 'creature' ? l.snapshot as CreatureDTO : null;
  const q = l.kind === 'held' ? (l.snapshot as HeldItem).quality : l.kind === 'relic' ? (l.snapshot as Relic).quality : null;
  const stat = c ? gradeBadge(ratingOf(c.rolls, c.species)) : q != null ? `<span class="mk-qual">${q}${l.kind === 'held' ? '%' : ''}</span>` : '';
  const mid = c ? `<span class="mk-lv">${full(c.level)}</span>` : l.qty > 1 ? `<span class="mk-lv">×${full(l.qty)}</span>` : '';
  const when = hold ? `<span class="mk-hold">Draw · <span class="cd">${left(l.openAt - serverNow())}</span></span>` : `<span class="mk-when">${ago(l.openAt)} ago</span>`;
  const focus = l.id === S.marketFocus;
  return `<div class="mk-row${l.house ? ' house' : ''}${focus ? ' focus' : ''}"${focus ? ' id="mfocus"' : ''}${c ? ` data-listing-id="${l.id}"` : ''}>
    <span class="mk-art${d.mon ? ' crt' : ''}"><img src="${d.pic}" alt=""></span>
    <div class="mk-what"><div class="mk-nm">${cat === 'all' && c ? gradeBadge(ratingOf(c.rolls, c.species), 'sm') : ''}${d.name}${c ? `<span class="mk-lvi${cat === 'creature' ? ' mk-ph' : ''}">Lv ${full(c.level)}</span>` : l.qty > 1 && l.unit ? `<span class="mk-lvi mk-ph">×${full(l.qty)}</span>` : ''}</div><div class="mk-sub">${d.sub}</div></div>
    <div class="mk-stat">${cat === 'all' ? (q != null ? stat : '') : stat}</div>
    <div class="mk-mid r">${cat === 'all' && c ? '' : mid}</div>
    <div class="mk-by">${sellerHtml(l)}${when}</div>
    <div class="mk-price r">${money(each(l), curOf(l))}${l.qty > 1 && !l.unit ? `<span class="mk-tot">lot of ${full(l.qty)}</span>` : l.qty > 1 ? '<span class="mk-tot">each</span>' : ''}</div>
    <div class="mk-act">${buyButton(l)}</div></div>`;
}

/** An item's page, in one currency. */
const itemHref = (ref: string, cur: MarketCurrency = 'coins') => `#/market/item/${encodeURIComponent(ref)}${cur === 'gems' ? ':gems' : ''}`;

function groupRow(g: Extract<MarketRow, { t: 'group' }>) {
  const it = ITEM[g.ref];
  const trend = g.avg ? (g.low < g.avg * 0.97 ? '<i class="mk-dn" title="Below the 7-day average">▼</i>' : g.low > g.avg * 1.03 ? '<i class="mk-up" title="Above the 7-day average">▲</i>' : '') : '';
  return `<a class="mk-row grp" href="${itemHref(g.ref, g.cur)}">
    <span class="mk-art"><img src="${iconUrl(g.ref)}" alt=""></span>
    <div class="mk-what"><div class="mk-nm"><b>${esc(it?.name ?? g.ref)}</b></div><div class="mk-sub">${esc(it?.desc ?? '')}</div></div>
    <div class="mk-stat"><span class="mk-cnt">${full(g.n)} ${g.n === 1 ? 'listing' : 'listings'}<span class="mk-ph"> · ${full(g.qty)} on offer</span></span></div>
    <div class="mk-mid r"><span class="mk-lv">${full(g.qty)}</span></div>
    <div class="mk-by r">${g.avg ? `<span class="mk-avg"><span class="mk-ph">7-day avg </span>${money(g.avg, g.cur)}</span>` : '<span class="mk-when mk-nph">–</span>'}</div>
    <div class="mk-price r"><span class="mk-from">from</span>${money(g.low, g.cur)}${trend}</div>
    <div class="mk-act"><span class="mk-go" aria-hidden="true">›</span></div></a>`;
}

/** A group shown in Everything reads the same columns as a listing: item, qty on offer, "n sellers", lowest price. */
function groupRowAll(g: Extract<MarketRow, { t: 'group' }>) {
  const it = ITEM[g.ref];
  return `<a class="mk-row grp" href="${itemHref(g.ref, g.cur)}">
    <span class="mk-art"><img src="${iconUrl(g.ref)}" alt=""></span>
    <div class="mk-what"><div class="mk-nm"><b>${esc(it?.name ?? g.ref)}</b></div><div class="mk-sub">${esc(it?.desc ?? '')}</div></div>
    <div class="mk-stat"></div>
    <div class="mk-mid r"><span class="mk-lv">${full(g.qty)}</span></div>
    <div class="mk-by"><span class="mk-cnt">${full(g.n)} ${g.n === 1 ? 'seller' : 'sellers'}<span class="mk-ph"> · ${full(g.qty)} on offer</span></span></div>
    <div class="mk-price r"><span class="mk-from">from</span>${money(g.low, g.cur)}</div>
    <div class="mk-act"><span class="mk-go" aria-hidden="true">›</span></div></a>`;
}

// ---------------- sidebar ----------------
const seg = (key: keyof MarketQuery, opts: [string, string][], cur: unknown) =>
  `<div class="mk-seg" role="group">${opts.map(([v, n]) => `<button class="${String(cur ?? '') === v ? 'on' : ''}" data-mset="${key}" data-v="${esc(v)}" aria-pressed="${String(cur ?? '') === v}">${n}</button>`).join('')}</div>`;
const range = (a: keyof MarketQuery, b: keyof MarketQuery, ph: [string, string]) =>
  `<div class="mk-range"><input type="number" min="0" inputmode="numeric" data-mf="${a}" value="${M.q[a] ?? ''}" placeholder="${ph[0]}" aria-label="${ph[0]}"><span>–</span><input type="number" min="0" inputmode="numeric" data-mf="${b}" value="${M.q[b] ?? ''}" placeholder="${ph[1]}" aria-label="${ph[1]}"></div>`;

function filtersHtml(cat: MarketCat) {
  const q = M.q;
  const out: string[] = [];
  if (cat === 'creature') {
    out.push(`<h5>Element</h5><div class="mk-els">${ELEMENT_IDS.map((e) => `<button class="mk-el${q.el === e ? ' on' : ''}" data-mset="el" data-v="${q.el === e ? '' : e}" style="--el:${ELEMENTS[e].color}" aria-pressed="${q.el === e}">${ELEMENTS[e].name}</button>`).join('')}</div>`);
    out.push(`<h5>Region</h5>${seg('region', [['', 'Any'], ...REGIONS.map((r) => [r.id, r.name] as [string, string])], q.region)}`);
    const grades = GRADE_CUTOFFS.filter(([g]) => g !== 'C').reverse();
    out.push(`<h5>Grade, at least</h5>${seg('minScore', [['', 'Any'], ...grades.map(([g, min]) => [String(min), g] as [string, string])], q.minScore)}`);
    out.push(`<h5>Bloodline, at least</h5>${seg('blood', [['', 'Any'], ...[1, 2, 3, 4].map((b) => [String(b), CONFIG.bloodline[b].name] as [string, string])], q.blood)}`);
    out.push(`<label class="mk-check"><input type="checkbox" data-mf="aura"${q.aura ? ' checked' : ''}><span>Aura only</span></label>`);
    out.push(`<h5>Level</h5>${range('lvMin', 'lvMax', ['Min', 'Max'])}`);
  }
  if (cat === 'held' || cat === 'relic') out.push(`<h5>Quality, at least</h5>${seg('minQ', [['', 'Any'], ...(cat === 'held' ? [70, 80, 90] : [50, 75, 90]).map((v) => [String(v), String(v)] as [string, string])], q.minQ)}`);
  out.push(`<h5>Price each</h5>${range('pMin', 'pMax', ['Min', 'Max'])}`);
  // The Exchange's filter only while it trades (retired 2026-10-20: the server sends no Exchange then).
  if (S.market?.exchange || q.seller) out.push(`<h5>Seller</h5>${seg('seller', [['', 'All'], ['players', 'Players'], ['house', 'Exchange']], q.seller)}`);
  return out.join('');
}

/** Filters set besides the category and sort, each with its label. */
function activeFilters(): [keyof MarketQuery, string][] {
  const q = M.q, chips: [keyof MarketQuery, string][] = [];
  if (q.q) chips.push(['q', `“${q.q}”`]);
  if (q.el) chips.push(['el', ELEMENTS[q.el as keyof typeof ELEMENTS]?.name ?? q.el]);
  if (q.region) chips.push(['region', REGIONS.find((r) => r.id === q.region)?.name ?? q.region]);
  if (q.minScore) chips.push(['minScore', `Grade ${GRADE_CUTOFFS.find(([, m]) => m === q.minScore)?.[0] ?? ''} or better`]);
  if (q.blood) chips.push(['blood', `Bloodline ${CONFIG.bloodline[q.blood]?.name ?? ''} or better`]);
  if (q.aura) chips.push(['aura', 'Aura']);
  if (q.lvMin || q.lvMax) chips.push(['lvMin', `Lv ${q.lvMin ?? 1}–${q.lvMax ?? '∞'}`]);
  if (q.minQ) chips.push(['minQ', `Quality ${q.minQ} or better`]);
  if (q.pMin || q.pMax) chips.push(['pMin', `${q.pMin ? full(q.pMin) : '0'}–${q.pMax ? full(q.pMax) : '∞'} each`]);
  if (q.seller) chips.push(['seller', q.seller === 'house' ? 'Exchange' : 'Players']);
  return chips;
}

/** The filters set, as removable chips above the board. */
function activeChips(): string {
  const chips = activeFilters();
  if (!chips.length) return '';
  return `<div class="mk-chips">${chips.map(([k, n]) => `<button class="mk-chip" data-mclear="${k}">${esc(n)}<i>×</i></button>`).join('')}${chips.length > 1 ? '<button class="mk-chip clear" data-mclear="*">Clear all</button>' : ''}</div>`;
}

const SORT_NAMES: Record<MarketSort, string> = { price: 'Price: low to high', '-price': 'Price: high to low', new: 'Newest', grade: 'Grade', level: 'Level', quality: 'Quality', qty: 'Most on offer' };
function sortsFor(cat: MarketCat): MarketSort[] {
  if (cat === 'creature') return ['price', '-price', 'new', 'grade', 'level'];
  if (cat === 'held' || cat === 'relic') return ['price', '-price', 'new', 'quality'];
  if (cat === 'shrine') return ['price', '-price', 'new'];
  if (cat === 'all') return ['price', '-price', 'new', 'qty'];
  return ['price', '-price', 'new', 'qty'];
}

// ---------------- Browse ----------------
function browsePane() {
  const m = S.market;
  const cat = M.q.cat ?? 'all';
  const counts = m?.counts ?? {};
  const open = curs();
  const cats = `<nav class="mk-cats" aria-label="Categories">${MARKET_CATS.map((c) => `<button class="mk-cat${cat === c.id ? ' on' : ''}" data-mset="cat" data-v="${c.id}" aria-pressed="${cat === c.id}"><span class="mc-ico">${CAT_ICON[c.id]()}</span><span class="mc-nm">${c.name}</span><span class="mc-n">${counts[c.id] ? full(counts[c.id]!) : ''}</span></button>`).join('')}</nav>`;
  const nf = activeFilters().filter(([k]) => k !== 'q').length;
  const side = `<aside class="mk-side${M.fopen ? ' fopen' : ''}">
      <label class="mk-search">${ICON.search}<input data-msearch value="${esc(M.q.q ?? '')}" placeholder="Search the market" aria-label="Search by name or seller" autocomplete="off"></label>
      ${cats}
      <button class="mk-fsum" data-act="mk-fopen" aria-expanded="${M.fopen}">Filters${nf ? `<b>${nf} on</b>` : ''}<span aria-hidden="true">${M.fopen ? '▴' : '▾'}</span></button>
      <div class="mk-filters">${filtersHtml(cat)}</div></aside>`;
  const sorts = sortsFor(cat);
  const sort = sorts.includes(M.q.sort ?? 'price') ? M.q.sort ?? 'price' : 'price';
  const bar = `<div class="mk-bar">
      <h2 class="mk-title">${esc(MARKET_CATS.find((c) => c.id === cat)?.name ?? '')}${m ? `<span>${full(m.total)}</span>` : ''}</h2>
      ${open.length > 1 ? `<div class="mk-seg cur" role="group" aria-label="Currency"><button class="${!M.q.cur ? 'on' : ''}" data-mset="cur" data-v="" aria-pressed="${!M.q.cur}">All</button>${MARKET_CURRENCIES.filter((c) => open.includes(c.id)).map((c) => `<button class="${M.q.cur === c.id ? 'on' : ''}" data-mset="cur" data-v="${c.id}" aria-pressed="${M.q.cur === c.id}"><img src="${curIcon(c.id)}" alt="">${c.name}</button>`).join('')}</div>` : ''}
      <label class="mk-sortsel"><span>Sort</span><select data-msort-sel aria-label="Sort">${sorts.map((s) => `<option value="${s}"${sort === s ? ' selected' : ''}>${SORT_NAMES[s]}</option>`).join('')}</select></label></div>`;
  let board: string;
  if (!m) board = '<p class="mk-empty">Loading…</p>';
  else {
    const rows = m.rows.map((r) => {
      if (r.t === 'group') return cat === 'all' ? groupRowAll(r) : groupRow(r);
      const l = byId(r.id);
      return l ? oneRow(l, cat) : '';
    }).join('');
    const focus = S.marketFocus ? byId(S.marketFocus) : undefined;
    const pinned = focus && !m.rows.some((r) => r.t === 'one' && r.id === focus.id) ? `<div class="mk-pin">${oneRow(focus, 'all')}</div>` : '';
    const gone = S.marketFocus && !focus ? '<p class="mk-note">That listing has sold or ended.</p>' : '';
    const empty = `<p class="mk-empty">${m.total || Object.keys(counts).length ? 'Nothing matches these filters.' : 'Nothing for sale here yet.'}</p>`;
    board = `${gone}${pinned}<div class="mk-board ${cat === 'all' ? 'all' : ['stones', 'materials', 'charms', 'keys'].includes(cat) ? 'goods' : cat}">${rows ? headRow(cat) + rows : empty}</div>${pager(m.page, m.pages)}`;
  }
  return `<div class="mkt">${side}<section class="mk-main">${bar}${activeChips()}${board}</section></div>`;
}

function pager(page: number, pages: number) {
  if (pages <= 1) return '';
  const btn = (p: number, label: string, on = false) => `<button class="mk-pg${on ? ' on' : ''}" data-mpage="${p}"${p < 0 || p >= pages ? ' disabled' : ''}>${label}</button>`;
  const near = [...new Set([0, page - 1, page, page + 1, pages - 1])].filter((p) => p >= 0 && p < pages).sort((a, b) => a - b);
  const nums: string[] = [];
  near.forEach((p, i) => { if (i && p - near[i - 1] > 1) nums.push('<span class="mk-gap">…</span>'); nums.push(btn(p, String(p + 1), p === page)); });
  return `<nav class="mk-pager" aria-label="Pages">${btn(page - 1, '‹ Prev')}${nums.join('')}${btn(page + 1, 'Next ›')}</nav>`;
}

function recentSales() {
  const sold = (S.market?.sold ?? []).slice(0, 12);
  if (!sold.length) return '';
  return `<h4 class="sec">Recent sales</h4><ul class="mk-tape" data-fit-x="mk-tape" data-fit="mk-tape">${sold.map((l) => {
    const d = describe(l);
    const c = l.kind === 'creature' ? l.snapshot as CreatureDTO : null;
    const g = c ? gradeOf(ratingOf(c.rolls, c.species)) : '';
    return `<li data-key="tape-${l.id}"><span class="mk-tart${d.mon ? ' crt' : ''}"><img src="${d.pic}" alt=""></span><span class="mk-tn">${g ? gradeBadge(ratingOf(c!.rolls, c!.species), 'sm') : ''}<span class="mk-tnm">${esc(d.plain)}</span>${l.qty > 1 ? `<i>×${full(l.qty)}</i>` : ''}</span>${money(l.price, curOf(l))}</li>`;
  }).join('')}</ul>`;
}

// ---------------- an item's page ----------------
/** Plan a buy from the book like the server does (shared/market-query.ts planBuy). */
function plan(book: ListingDTO[], qty: number) {
  const now = serverNow();
  let units = 0, total = 0, top = 0;
  for (const l of book) {
    if (units >= qty) break;
    if (l.openAt > now || !l.unit) continue;
    const n = Math.min(l.qty, qty - units);
    units += n; total += n * l.unit; top = Math.max(top, l.unit);
  }
  return { units, total, top };
}

/** Daily averages over the price window, as a filled line with its low and high marked. */
function chart(v: (number | null)[]) {
  const pts = v.map((y, i) => [i, y] as const).filter((p): p is readonly [number, number] => p[1] != null);
  // A line needs two days. One day of sales is not "no sales": the stats under the chart already count them.
  if (pts.length < 2) return `<div class="mk-chart none">${pts.length ? 'Sales on one day only' : `No sales in ${PRICE_WINDOW_DAYS} days`}</div>`;
  const lo = Math.min(...pts.map((p) => p[1])), hi = Math.max(...pts.map((p) => p[1]));
  const W = 560, H = 150, P = 8;
  const x = (i: number) => P + (i / (v.length - 1)) * (W - 2 * P);
  const y = (n: number) => hi === lo ? H / 2 : H - 18 - ((n - lo) / (hi - lo)) * (H - 40);
  const line = pts.map(([i, n]) => `${x(i).toFixed(1)},${y(n).toFixed(1)}`).join(' ');
  const area = `${x(pts[0][0]).toFixed(1)},${H - 10} ${line} ${x(pts[pts.length - 1][0]).toFixed(1)},${H - 10}`;
  const grid = [0.25, 0.5, 0.75].map((f) => `<line x1="${P}" x2="${W - P}" y1="${(22 + f * (H - 40)).toFixed(1)}" y2="${(22 + f * (H - 40)).toFixed(1)}"/>`).join('');
  const last = pts[pts.length - 1];
  return `<div class="mk-chart"><div class="mk-plot"><svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img" aria-label="Average price per day, last ${v.length} days">
      <g class="g">${grid}</g><polygon class="a" points="${area}"/><polyline class="l" points="${line}"/></svg>
      <i class="mk-dot" style="left:${(x(last[0]) / W * 100).toFixed(2)}%;top:${(y(last[1]) / H * 100).toFixed(2)}%"></i></div>
      <span class="mk-hi">${full(hi)}</span><span class="mk-lo">${full(lo)}</span><span class="mk-x0">${v.length} days ago</span><span class="mk-x1">Today</span></div>`;
}

function itemPane() {
  const ref = M.q.item ?? '';
  const it = ITEM[ref];
  if (!it) return '<p class="mk-empty">Unknown item.</p>';
  const m = S.market;
  const book = (m?.book?.ref === ref ? m.book.ids : []).map(byId).filter((l): l is ListingDTO => !!l);
  const cur = M.icur, open = curs();
  const mine = (m?.mine ?? []).map(byId).filter((l): l is ListingDTO => !!l && l.kind === 'item' && l.ref === ref && curOf(l) === cur);
  const px = requestPrices('item', ref, false, cur);
  const st = px?.stats;
  const now = serverNow();
  const ready = book.filter((l) => l.openAt <= now && l.unit);
  const avail = ready.reduce((a, l) => a + l.qty, 0);
  const low = book.length ? Math.min(...book.map(each)) : null;
  const qty = Math.max(1, Math.min(M.buyQty, Math.max(1, avail)));
  const p = plan(book, qty);
  const armed = isArmed(`mkb:${ref}`);
  const short = purse(cur) < p.total;
  const have = tradableQty(ref);
  const ex = cur === 'coins' ? S.market?.exchange?.offers.find((o) => o.id === ref && o.buyLeft > 0) : undefined;
  const stat = (k: string, v: string) => `<div><i>${k}</i><b>${v}</b></div>`;
  const n = (v: number | null | undefined) => v == null ? '–' : full(v);
  const back = `<a class="mk-back" href="#/market">‹ ${esc(MARKET_CATS.find((c) => c.id === catOf('item', ref))?.name ?? 'Market')}</a>`;
  const hero = `<header class="mk-hero"><span class="mk-hart"><img src="${iconUrl(ref)}" alt=""></span>
      <div class="mk-htxt"><h2>${esc(it.name)}</h2><p>${esc(it.desc ?? '')}</p></div>
      <div class="mk-hprice">${open.length > 1 ? `<nav class="mk-icur" aria-label="Currency">${MARKET_CURRENCIES.filter((c) => open.includes(c.id)).map((c) => `<a class="${cur === c.id ? 'on' : ''}" href="${itemHref(ref, c.id)}"><img src="${curIcon(c.id)}" alt="">${c.name}</a>`).join('')}</nav>` : ''}${low != null ? `<i>Starting at</i>${money(low, cur, 'big')}<span>${full(book.reduce((a, l) => a + l.qty, 0))} on offer</span>` : '<i>None for sale</i>'}</div></header>`;
  const buy = `<section class="panel mk-buy"><h4>Buy</h4>
      ${avail ? `<label class="mk-qty"><span>Quantity</span><span class="mk-step"><button data-mqty="-1" aria-label="One fewer">−</button><input type="number" min="1" max="${avail}" value="${qty}" data-mbuy inputmode="numeric" aria-label="Quantity"><button data-mqty="1" aria-label="One more">+</button></span>${avail >= 10 ? '<button class="mk-max" data-mqty="=10">×10</button>' : ''}${avail >= 100 ? '<button class="mk-max" data-mqty="=100">×100</button>' : ''}<button class="mk-max" data-mqty="max">Max ${full(avail)}</button></label>
      <dl class="mk-sum"><div><dt>Average each</dt><dd>${money(Math.round(p.total / Math.max(1, p.units)), cur)}</dd></div><div class="tot"><dt>Total</dt><dd>${money(p.total, cur, 'big')}${cur === 'gems' ? `<span class="mk-usd">${gemUsd(p.total)}</span>` : ''}</dd></div></dl>
      <button class="btn ${armed ? 'gold armed' : 'primary'} mk-buybtn" data-act="mk-buybest" data-id="${esc(ref)}"${short || testAccount() ? ' disabled' : ''}${testAccount() ? ` title="${TEST_ACCOUNT}"` : ''}>${testAccount() ? 'Test account: no trading' : short ? `Not enough ${curWord(cur)}` : armed ? `Confirm ${full(p.total)} ${curWord(cur, p.total)}` : `Buy ${full(p.units)} for ${full(p.total)}`}</button>`
      : `<p class="mk-none">${book.length ? 'Every listing is still in its draw.' : 'Nobody is selling this right now.'}</p>`}
      <div class="mk-own"><span>You have <b>${full(have)}</b>${cur === 'gems' ? ` · ${money(purse('gems'), 'gems')}` : ''}</span>${have ? `<button class="btn small" data-act="list-item" data-id="${esc(ref)}"${cur === 'gems' ? ' data-cur="gems"' : ''}>Sell yours</button>` : ''}</div>
      ${ex && have ? `<div class="mk-own ex"><span><span class="mk-house">Exchange</span> buys at ${money(ex.bid)}</span><button class="btn small" data-act="ex-sell" data-id="${esc(ref)}">Sell</button></div>` : ''}
      ${mine.length ? `<div class="mk-own"><span>You list <b>${full(mine.reduce((a, l) => a + l.qty, 0))}</b> at ${money(each(mine[0]), cur)}</span><a class="btn small" href="#/market/mine">Yours</a></div>` : ''}</section>`;
  const hist = `<section class="panel mk-hist"><div class="mk-histhead"><h4>Price history</h4><button class="link" data-act="mk-pricetable" data-id="${esc(ref)}">Every sale</button></div>
      ${px ? chart(px.spark) : '<div class="mk-chart none">Loading…</div>'}
      <div class="mk-stats">${stat('Last sale', n(st?.last))}${stat('7-day avg', n(st?.avg7))}${stat(`${PRICE_WINDOW_DAYS}-day low`, n(st?.min))}${stat(`${PRICE_WINDOW_DAYS}-day high`, n(st?.max))}${stat(`Sold in ${PRICE_WINDOW_DAYS} days`, st ? full(st.units) : '–')}</div></section>`;
  const orders = book.length ? `<div class="mk-book">
      <div class="mk-bh" aria-hidden="true"><span class="r">Price each</span><span class="r">Qty</span><span>Seller</span><span></span></div>
      <div class="mk-brs" data-fit="mk-book-${esc(ref)}">${book.map((l) => `<div class="mk-br${l.house ? ' house' : ''}${l.openAt > now ? ' held' : ''}"><span class="r">${money(each(l), cur)}</span><span class="r mk-lv">${full(l.qty)}</span><span class="mk-by">${sellerHtml(l)}${l.openAt > now ? `<span class="mk-hold">Draw · <span class="cd">${left(l.openAt - now)}</span></span>` : `<span class="mk-when">${ago(l.openAt)} ago</span>`}</span><span class="mk-act">${buyButton(l)}</span></div>`).join('')}</div></div>` : '<p class="mk-none">No sell orders.</p>';
  // Phones: Buy, Orders and History take turns under tabs (mki). Wider: Buy stays at left, Orders and History
  // take turns at right (mkr). Each set's tabs show only at its own sizes (public/fit.css).
  const pk = ['buy', 'orders', 'hist'], rk = ['orders', 'hist'];
  const ph = (key: string, html: string) => psecWrap('mki', key, pk, html);
  const rt = (key: string, html: string) => psecWrap('mkr', key, rk, html);
  const tabs = psecTabs('mki', [{ key: 'buy', title: 'Buy' }, { key: 'orders', title: `Sell orders ${book.length}` }, { key: 'hist', title: 'Price history' }]);
  const rtabs = psecTabs('mkr', [{ key: 'orders', title: `Sell orders ${book.length}` }, { key: 'hist', title: 'Price history' }]);
  return `${back}${hero}${tabs}<div class="mk-itemgrid">${ph('buy', buy)}<div class="mk-iright">${rtabs}${rt('orders', ph('orders', orders))}${rt('hist', ph('hist', hist))}</div></div>`;
}

// ---------------- Sell, Yours, History ----------------
function sellPane() {
  const t = S.trainer!;
  const items = ITEMS.filter((i) => i.tradable && tradableQty(i.id) > 0);
  const held = (t.held ?? []).filter((h) => !h.dev);
  const relics = t.relics.filter((r) => !r.dev);
  const shrines = (t.shrines ?? []).filter((s) => !s.dev);
  const lead = new Set(t.team), favs = new Set(t.collection ?? []);
  const mons = [...S.creatures.values()].filter((c) => !c.listed && !c.bound && !c.devGranted && !lead.has(c.id) && !favs.has(c.id))
    .sort((a, b) => ratingOf(b.rolls, b.species) - ratingOf(a.rolls, a.species)).slice(0, 24);
  const card = (pic: string, name: string, sub: string, act: string, id: string, mon = false, extra = '') =>
    `<div class="mk-sc${extra ? ' two' : ''}" data-key="${act}-${esc(id)}"><span class="mk-art${mon ? ' crt' : ''}"><img src="${pic}" alt=""></span><div class="mk-what"><div class="mk-nm">${name}</div><div class="mk-sub">${sub}</div></div><div class="mk-scact">${extra}<button class="btn small primary" data-act="${act}" data-id="${esc(id)}">List</button></div></div>`;
  // Each kind is a tab whose cards page, so a big bag never scrolls.
  const sec = (key: string, title: string, rows: string[]) => ({ key, title, html: rows.length ? `<div class="mk-sells" data-fit="mks-${key}">${rows.join('')}</div>` : '' });
  const open = (S.market?.mine ?? []).length;
  const exBids = new Map((S.market?.exchange?.offers ?? []).filter((o) => o.buyLeft > 0 && o.mineLeft > 0).map((o) => [o.id, o.bid]));
  const out = phoneSections('mksell', [
    sec('items', 'Items', items.map((i) => {
      // Items the Exchange is buying today offer that too, at its bid.
      const ex = exBids.get(i.id);
      return card(iconUrl(i.id), `<b>${esc(i.name)}</b>`, `You have ${full(tradableQty(i.id))}`, 'list-item', i.id, false,
        `${ex ? `<button class="btn small mk-ex" data-act="ex-sell" data-id="${esc(i.id)}" title="The Exchange buys at ${full(ex)} each">Sell to Exchange <img src="${iconUrl('coin')}" alt="">${full(ex)}</button>` : ''}<a class="btn small" href="#/market/item/${encodeURIComponent(i.id)}">Prices</a>`);
    })),
    sec('held', 'Held items', held.map((h) => card(iconUrl(h.item), `<b>${esc(heldName(h))}</b>`, esc(heldText(h)), 'list-held', h.id))),
    sec('relics', 'Relics', relics.map((r) => card(iconUrl(`relic-${r.stat}`), `${relicGradeBadge(r, 'sm')}<b>${esc(relicName(r))}</b> <span class="muted">Q${r.quality}</span>`, esc(relicLine(r)), 'list-relic', r.id))),
    sec('shrines', 'Shrines', shrines.map((s) => card(iconUrl('shrine-shard'), `<b>${esc(SHRINE_TIERS[s.tier].name)}</b>`, s.active ? 'In use' : '', 'list-shrine', s.id))),
    sec('creatures', 'Creatures', mons.map((c) => card(spriteUrl(c.species, c.rolls.aura), `${gradeBadge(ratingOf(c.rolls, c.species), 'sm')}<b>${esc(cname(c))}</b>${crest(c.rolls.bloodline)}${c.rolls.aura ? auraTag() : ''}`, `Lv ${full(c.level)} ${SPECIES[c.species].elements.map(elChip).join('')}`, 'list-creature', String(c.id), true))),
  ], undefined, true);
  return `<div class="mk-slots"><b>${open}</b> of ${MARKET_MAX_OPEN} listings open</div>${out || '<p class="mk-empty">Nothing you hold trades right now.</p>'}`;
}

function minePane() {
  const mine = (S.market?.mine ?? []).map(byId).filter((l): l is ListingDTO => !!l);
  const now = serverNow();
  const rows = mine.map((l) => {
    const d = describe(l);
    const c = l.kind === 'creature' ? l.snapshot as CreatureDTO : null;
    const state = l.openAt > now ? `<span class="mk-hold">Draw · <span class="cd">${left(l.openAt - now)}</span></span>` : `<span class="mk-when">Ends in ${left(l.expires - now)}</span>`;
    return `<div class="mk-row mine"${c ? ` data-listing-id="${l.id}"` : ''}><span class="mk-art${d.mon ? ' crt' : ''}"><img src="${d.pic}" alt=""></span>
      <div class="mk-what"><div class="mk-nm">${c ? gradeBadge(ratingOf(c.rolls, c.species), 'sm') : ''}${d.name}${c ? `<span class="mk-lvi">Lv ${full(c.level)}</span>` : l.qty > 1 ? `<span class="mk-lvi mk-ph">×${full(l.qty)}</span>` : ''}</div><div class="mk-sub">${d.sub}</div></div>
      <div class="mk-mid r">${l.qty > 1 ? `<span class="mk-lv">×${full(l.qty)}</span>` : ''}</div>
      <div class="mk-by">${state}</div>
      <div class="mk-price r">${money(each(l), curOf(l))}${l.qty > 1 ? `<span class="mk-tot">${full(l.price)} for all</span>` : ''}</div>
      <div class="mk-act"><button class="btn small" data-act="share-listing" data-id="${l.id}">Share in Trade</button><button class="btn small" data-act="unlist" data-id="${l.id}">Cancel</button></div></div>`;
  }).join('');
  return `<div class="mk-slots"><b>${mine.length}</b> of ${MARKET_MAX_OPEN} listings open<a class="btn small primary" href="#/market/sell">Sell something</a></div>
    ${rows ? `<div class="mk-board mine" data-fit="mk-mine">${rows}</div>` : '<p class="mk-empty">You have nothing listed.</p>'}`;
}

function historyPane() {
  const trades = S.market?.trades ?? [];
  if (!trades.length) return '<p class="mk-empty">No sales or purchases yet.</p>';
  return `<div class="mk-board hist"><div class="mk-head hh" aria-hidden="true"><span></span><span>Trade</span><span>When</span><span class="r">Amount</span></div><div class="mk-hrows" data-fit="mk-hist">${trades.map((l) => {
    const d = describe(l);
    const c = l.kind === 'creature' ? l.snapshot as CreatureDTO : null;
    const cur = curOf(l);
    const net = l.bought ? l.price : l.price - (cur === 'gems' ? gemFee(l.kind, l.price) : Math.ceil(l.price * CONFIG.marketFeeCoins));
    return `<div class="mk-row"><span class="mk-art${d.mon ? ' crt' : ''}"><img src="${d.pic}" alt=""></span>
      <div class="mk-what"><div class="mk-nm">${c ? gradeBadge(ratingOf(c.rolls, c.species), 'sm') : ''}<b>${esc(d.plain)}</b>${l.qty > 1 ? ` <span class="mk-lvi">×${full(l.qty)}</span>` : ''}</div><div class="mk-sub">${l.bought ? `Bought from ${l.house ? 'the Exchange' : esc(l.seller)}` : 'Sold'}</div></div>
      <div class="mk-by"><span class="mk-when">${ago(l.soldAt ?? l.openAt)} ago</span></div>
      <div class="mk-price r">${flow(l.bought ? -net : net, cur)}</div></div>`;
  }).join('')}</div></div>`;
}

// ---------------- Gems ----------------
const GEM_KINDS: Record<string, string> = { 'buy-pack': 'Gems bought', spend: 'Bought', sell: 'Sold', cashout: 'Cash-out', 'cashout-back': 'Cash-out returned', refund: 'Pack refunded', dispute: 'Card payment disputed', admin: 'Adjustment' };
const W = { gems: '' };
const cents = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The gem wallet: bought and earned gems, gem packs, cash-out of earned gems through Stripe, and every movement. */
function gemsPane() {
  const g = S.market?.gems;
  if (!g) return '<p class="mk-empty">Gems are not open.</p>';
  const n = Math.floor(Number(W.gems) || 0);
  const okAmt = n >= GEM_WITHDRAW_MIN && n <= g.cashable;
  const queued = g.withdrawals.filter((w) => w.status === 'queued');
  const done = g.withdrawals.filter((w) => w.status !== 'queued');
  const status: Record<string, string> = { sent: 'Paid', cancelled: 'Cancelled', refused: 'Refused, returned' };
  const kindName = (r: { kind: string; ref?: string }) => {
    const [k, ref] = (r.ref ?? '').split(':');
    if (k === 'diamonds') return `Diamonds · ${full(packById(ref)?.diamonds ?? 0)}`;
    const what = k === 'item' ? ITEM[ref]?.name : k === 'creature' ? 'a creature' : k === 'held' ? 'a held item' : k === 'relic' ? 'a relic' : k === 'shrine' ? 'a shrine' : '';
    return `${GEM_KINDS[r.kind] ?? r.kind}${what && (r.kind === 'spend' || r.kind === 'sell') ? ` · ${esc(what)}` : ''}`;
  };
  const bought = g.bal - g.earned, waiting = g.earned - g.cashable;
  const stat = (label: string, v: number, sub = '') => `<div class="mk-wstat"><i>${label}</i><b>${full(v)}</b>${sub ? `<span>${sub}</span>` : ''}</div>`;
  const head = `<section class="mk-wallet"><div class="mk-wbal"><span class="mk-wico"><img src="${curIcon('gems')}" alt=""></span><div><i>Balance</i><b>${full(g.bal)}</b><span>${gemUsd(g.bal)}</span></div></div>
    <div class="mk-wstats">${stat('Bought', bought)}${stat('Earned', g.earned, waiting > 0 ? `${full(waiting)} settling` : '')}${stat('Cashable', g.cashable, g.cashable ? cashoutUsd(g.cashable) : '')}</div></section>`;
  const packs = g.packs.length
    ? `<div class="mk-packs">${g.packs.map((p) => `<button class="mk-pack" data-act="pack-buy" data-id="${esc(p.id)}"><img src="${curIcon('gems')}" alt=""><b>${full(p.gems)}</b><span>${cents(p.cents)}</span></button>`).join('')}</div>`
    : '<p class="mk-none">Gem packs open with the cash market.</p>';
  const buy = `<section class="panel mk-wpanel"><h4>Buy gems</h4>${packs}</section>`;
  let cash: string;
  if (!g.cashOpen) cash = '<p class="mk-none">Cash-out opens with the cash market.</p>';
  else if (g.payee !== 'ready') cash = `${g.payee === 'pending' ? '<p class="mk-wline">Stripe is checking your details.</p>' : ''}
    <button class="btn primary mk-buybtn" data-act="mk-payee">${g.payee === 'pending' ? 'Continue with Stripe' : 'Set up payouts'}</button>`;
  else cash = `<label class="mk-wf"><span>Gems</span><span class="mk-step"><input type="number" min="${GEM_WITHDRAW_MIN}" max="${g.cashable}" value="${esc(W.gems)}" data-mgem="gems" inputmode="numeric" placeholder="At least ${full(GEM_WITHDRAW_MIN)}"></span><button class="mk-max" data-act="mk-gemmax">Max</button></label>
    <dl class="mk-sum"><div class="tot"><dt>You receive</dt><dd class="mk-wtot">${n ? cashoutUsd(n) : '–'}</dd></div></dl>
    <button class="btn ${isArmed('mkw') ? 'gold armed' : 'primary'} mk-buybtn" data-act="mk-cashout"${okAmt ? '' : ' disabled'}>${isArmed('mkw') ? `Confirm ${full(n)} gems for ${cashoutUsd(n)}` : 'Cash out'}</button>`;
  const queue = queued.length ? `<ul class="mk-wq">${queued.map((w) => `<li>${money(w.gems, 'gems')}<span class="mk-when">${cents(w.cents)} · ${ago(w.created)} ago</span><span class="mk-hold">Queued</span><button class="btn small" data-act="mk-gemcancel" data-id="${w.id}">Cancel</button></li>`).join('')}</ul>` : '';
  const out = `<section class="panel mk-wpanel"><h4>Cash out</h4>${cash}${queue}</section>`;
  const rows = g.history.map((r) => `<div class="mk-row"><span class="mk-art"><img src="${curIcon('gems')}" alt=""></span><div class="mk-what"><div class="mk-nm"><b>${kindName(r)}</b></div><div class="mk-sub">Balance after ${full(r.bal)}</div></div><div class="mk-by"><span class="mk-when">${ago(r.ts)} ago</span></div><div class="mk-price r">${flow(r.delta, 'gems')}</div></div>`);
  const hist = rows.length ? `<div class="mk-board hist" data-fit="mk-ghist">${rows.join('')}</div>` : '';
  const past = done.length ? `<ul class="mk-wq past" data-fit="mk-gpast">${done.map((w) => `<li>${money(w.gems, 'gems')}<span class="mk-when">${cents(w.cents)} · ${ago(w.done ?? w.created)} ago</span><span class="mk-wst ${w.status}">${status[w.status] ?? w.status}</span></li>`).join('')}</ul>` : '';
  // Buying and cashing out first; the gem history and past cash-outs under their own tabs, so nothing scrolls.
  const secs = [
    { key: 'wallet', title: 'Buy and cash out', html: `<div class="mk-wgrid">${buy}${out}</div>` },
    ...(hist ? [{ key: 'hist', title: 'History', html: hist }] : []),
    ...(past ? [{ key: 'past', title: 'Cash-outs', html: past }] : []),
  ];
  return `${head}${phoneSections('mkg', secs, undefined, true)}`;
}

// ---------------- the page ----------------
export function marketHead() {
  const n = S.market?.mine.length ?? 0;
  const tab = (id: Tab, path: string, name: string, extra = '') => `<a href="#/market${path}" class="${M.tab === id || (id === 'browse' && M.tab === 'item') ? 'on' : ''}">${name}${extra}</a>`;
  return {
    meta: '',
    tabs: `<nav class="subtabs mk-tabs">${tab('browse', '', 'Browse')}${tab('sell', '/sell', 'Sell')}${tab('mine', '/mine', '<span class="st-long">Your listings</span><span class="st-short">Listings</span>', n ? ` <span class="mk-tabn">${n}</span>` : '')}${tab('history', '/history', 'History')}${tab('prices', '/prices', '<span class="st-long">Price table</span><span class="st-short">Prices</span>')}${S.market?.gems ? tab('gems', '/gems', 'Gems', ` <span class="mk-tabg"><img src="${curIcon('gems')}" alt="">${full(S.market.gems.bal)}</span>`) : ''}</nav>`,
    actions: '<button class="btn small" data-act="market-refresh">Refresh</button>',
  };
}

export function marketPane() {
  const note = testAccount() ? `<p class="mk-test">${TEST_ACCOUNT}</p>` : '';
  return note + marketTab();
}

function marketTab() {
  switch (M.tab) {
    case 'item': return itemPane();
    case 'sell': return sellPane();
    case 'mine': return minePane();
    case 'history': return historyPane();
    // On a phone the price table and recent sales take turns, so the table has room to page.
    case 'prices': return phoneSections('mkpx', [{ key: 'table', title: 'Price table', html: priceSection() }, { key: 'recent', title: 'Recent sales', html: recentSales() }]);
    case 'gems': return gemsPane();
    default: return browsePane();
  }
}

/** Buy from a listing (rows here, hover cards, Trade cards): a stack opens its item's page. */
export function buyListing(id: number) {
  const l = byId(id);
  if (l && l.kind === 'item' && l.qty > 1 && l.unit) return go(`#/market/item/${encodeURIComponent(l.ref)}`);
  buyOne(id);
}

/** Buy one listing: a stack's buy takes one unit; Buy arms first, the second click buys. */
function buyOne(id: number) {
  const l = byId(id);
  if (!l) return send({ t: 'purchase', id });
  // A draw entry commits too (the winner pays when the hold ends), so it confirms like a buy.
  if (!confirmClick(`mk:${id}`, curOf(l) === 'gems' ? CONFIRM_MONEY_MS : undefined)) return;
  send({ t: 'purchase', id, qty: l.unit ? 1 : l.qty, unit: l.unit || l.price });
}

/** Clicks on the market page; true when handled. */
export function marketClick(el: HTMLElement): boolean {
  const ds = el.dataset;
  if (ds.mset !== undefined) {
    const k = ds.mset as keyof MarketQuery, v = ds.v ?? '';
    if (k === 'cat') { if (M.tab !== 'browse') go('#/market'); setQ({ cat: v as MarketCat, ...(sortsFor(v as MarketCat).includes(M.q.sort ?? 'price') ? {} : { sort: 'price' }) }); }
    else if (k === 'minScore' || k === 'blood' || k === 'minQ') setQ({ [k]: v ? Number(v) : undefined });
    else setQ({ [k]: v || undefined });
    return true;
  }
  if (ds.msort !== undefined) {
    const s = ds.msort as MarketSort, cur = M.q.sort ?? 'price';
    setQ({ sort: s === 'price' ? (cur === 'price' ? '-price' : 'price') : s });
    return true;
  }
  if (ds.mpage !== undefined) { setQ({ page: Number(ds.mpage) }, true); document.querySelector('.mk-main')?.scrollIntoView({ block: 'start', behavior: 'smooth' }); return true; }
  if (ds.mclear !== undefined) {
    const k = ds.mclear;
    if (k === '*') setQ({ q: undefined, el: undefined, region: undefined, minScore: undefined, blood: undefined, aura: undefined, lvMin: undefined, lvMax: undefined, minQ: undefined, pMin: undefined, pMax: undefined, seller: undefined });
    else if (k === 'lvMin') setQ({ lvMin: undefined, lvMax: undefined });
    else if (k === 'pMin') setQ({ pMin: undefined, pMax: undefined });
    else setQ({ [k]: undefined });
    return true;
  }
  if (ds.mqty !== undefined) {
    const v = ds.mqty;
    const max = Number(document.querySelector<HTMLInputElement>('[data-mbuy]')?.max) || 1;
    M.buyQty = v === 'max' ? max : v.startsWith('=') ? Math.min(max, Number(v.slice(1))) : Math.max(1, Math.min(max, M.buyQty + Number(v)));
    rerender(); return true;
  }
  switch (ds.act) {
    case 'mk-buy': buyOne(Number(ds.id)); return true;
    case 'mk-buybest': {
      const ref = ds.id ?? '';
      const book = (S.market?.book?.ref === ref ? S.market.book.ids : []).map(byId).filter((l): l is ListingDTO => !!l);
      const p = plan(book, M.buyQty);
      if (!p.units) return true;
      if (!confirmClick(`mkb:${ref}`, M.icur === 'gems' ? CONFIRM_MONEY_MS : undefined)) return true;
      send({ t: 'buyBest', item: ref, qty: p.units, maxUnit: p.top, maxTotal: p.total, ...(M.icur === 'gems' ? { cur: 'gems' as const } : {}) });
      // The server answers in order, so this fresh price table already counts the sale just made.
      requestPrices('item', ref, true, M.icur);
      return true;
    }
    case 'mk-gemmax': W.gems = String(S.market?.gems?.cashable ?? 0); rerender(); return true;
    case 'mk-cashout': {
      const n = Math.floor(Number(W.gems) || 0);
      if (!(n >= GEM_WITHDRAW_MIN) || n > (S.market?.gems?.cashable ?? 0)) return true;
      if (!confirmClick('mkw', CONFIRM_MONEY_MS)) return true;
      send({ t: 'gemCashout', gems: n });
      W.gems = '';
      return true;
    }
    case 'mk-payee': send({ t: 'gemPayee' }); return true;
    case 'mk-gemcancel': send({ t: 'gemCancel', id: Number(ds.id) }); return true;
    case 'mk-fopen': M.fopen = !M.fopen; rerender(); return true;
    case 'mk-pricetable': showPriceTable('item', ds.id ?? ''); go('#/market/prices'); return true;
  }
  return false;
}

/** Typing and picking on the market page; true when it was ours (the caller re-renders). */
export function marketInput(el: HTMLInputElement | HTMLSelectElement): boolean {
  if (el.dataset.msearch !== undefined) {
    // Search as you type, once the typing pauses.
    if (M.typing) clearTimeout(M.typing);
    const v = el.value;
    M.typing = setTimeout(() => { M.typing = 0; setQ({ q: v.trim() || undefined }); }, 220);
    return false;
  }
  if (el.dataset.msortSel !== undefined) { setQ({ sort: el.value as MarketSort }); return true; }
  if (el.dataset.mbuy !== undefined) { M.buyQty = Math.max(1, Math.floor(Number(el.value) || 1)); return true; }
  // The withdrawal form keeps its text across renders; re-rendering only updates the button and total.
  if (el.dataset.mgem === 'gems') {
    W.gems = el.value;
    const n = Math.floor(Number(W.gems) || 0), max = S.market?.gems?.cashable ?? 0;
    const btn = document.querySelector<HTMLButtonElement>('[data-act="mk-cashout"]'), tot = document.querySelector('.mk-wtot');
    if (btn) btn.disabled = !(n >= GEM_WITHDRAW_MIN && n <= max);
    if (tot) tot.textContent = n ? cashoutUsd(n) : '–';
    return false;
  }
  const k = el.dataset.mf as keyof MarketQuery | undefined;
  if (k) {
    if (k === 'aura') setQ({ aura: (el as HTMLInputElement).checked || undefined });
    else { const v = Math.floor(Number(el.value)); setQ({ [k]: v > 0 ? v : undefined }); }
    return true;
  }
  return false;
}
